import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { MibMenuEntry } from '@medinbox/angular';
import { InstantDatasService, UserService } from '@core/services';
import { PermissionService } from '@core/permissions';
import { Dialog } from '@angular/cdk/dialog';
import { TermsConditionsDialog } from '@shared/components/terms-conditions';

/** The route actually on screen: the leaf of the router tree, where `data` and params live. */
function deepestRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  while (route.firstChild) {
    route = route.firstChild;
  }
  return route;
}

/**
 * Headless account-menu logic. The panel itself is the library's `<mib-menu>`,
 * written by the call site next to its own trigger:
 *
 * ```html
 * <button mibButton [mibMenuTrigger]="accountMenu">…</button>
 * <mib-menu #accountMenu align="end" [menuItems]="mainMenu.entries()"
 *   (itemSelected)="mainMenu.onItemSelected($event)">
 *   <mib-avatar mibMenuHeader withName [username]="mainMenu.username()"></mib-avatar>
 * </mib-menu>
 * <mai-main-menu #mainMenu></mai-main-menu>
 * ```
 *
 * The menu owns its display conditions: the case entries appear on routes declaring
 * `data: { caseMenu: true }`, so no page has to feed it anything.
 */
@Component({
  selector: 'mai-main-menu',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainMenu {
  private readonly instantDatasService = inject(InstantDatasService);
  private readonly dialogService = inject(Dialog);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  protected readonly permissions = inject(PermissionService);

  private readonly activeRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => deepestRoute(this.router.routerState.snapshot.root)),
    ),
    { initialValue: deepestRoute(this.router.routerState.snapshot.root) },
  );

  /** The case being viewed, on routes declaring `caseMenu` — the id read off the route itself. */
  private readonly videoId = computed(() => {
    const route = this.activeRoute();
    return route.data['caseMenu'] ? (route.paramMap.get('id') ?? undefined) : undefined;
  });

  readonly username = computed(() => this.userService.currentUser()?.username);

  readonly entries = computed<MibMenuEntry[]>(() => {
    const entries: MibMenuEntry[] = [];
    if (this.permissions.canOpenLibrary()) {
      entries.push({ label: 'My case library', value: 'library', icon: 'library-big' });
    }
    if (this.permissions.canExportInstantData() && this.videoId()) {
      entries.push({ label: 'Download case context', value: 'instant-data', icon: 'code' });
    }
    entries.push({ label: 'Legal & Privacy', value: 'legal', icon: 'stamp' });
    if (this.permissions.canLogout()) {
      entries.push({ separator: true }, { label: 'Logout', value: 'logout', icon: 'log-out' });
    }
    return entries;
  });

  onItemSelected(value: string): void {
    switch (value) {
      case 'instant-data': {
        const videoId = this.videoId();
        if (this.permissions.canExportInstantData() && videoId) {
          this.instantDatasService.exportInstantData(videoId);
        }
        break;
      }
      case 'library':
        this.router.navigateByUrl('/');
        break;
      case 'legal':
        this.dialogService.open(TermsConditionsDialog, { ariaLabel: 'Legal information' });
        break;
      case 'logout':
        this.router.navigateByUrl('/logout');
        break;
    }
  }
}
